<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import AnLoader from '@/components/common/AnLoader.vue';
import AssetAllocationChart from '@/sections/analytics/AssetAllocationChart.vue';
import SectorBenchmarkChart from '@/sections/analytics/SectorBenchmarkChart.vue';
import SectorAttributionOverview from '@/sections/analytics/SectorAttributionOverview.vue';
import SectorAnalyticsDetail from '@/tabs/sector-analytics/SectorAnalyticsDetail.vue';
import GenerateRecommendationSummary from '@/components/analytics/GenerateRecommendationSummary.vue';
import { useUserStore } from '@/store/user-store';
import Chart from "primevue/chart";
import {
  chartColors,
} from "@/services/constant-services";

const store = useUserStore();
const portfolioAnalytics = computed(() => store.portfolioAnalyticsData || null);

// Chart data and state
const niftyChartData = ref({});
const niftyDonutOptions = ref({});
const isSectorClassificationOpen = ref(false);
const isMissingAssetsOpen = ref(false);
const activeRiskTab = ref('sharpe'); // Default to Sharpe Ratio
const activePerformanceTab = ref('twrr'); // Default to TWRR
const activeAllocationTab = ref('table'); // Default to table view
const activeSectorSection = ref(1); // 1 = Overview (PortfolioAnalytics), 2 = Detail (SectorAnalyticsDetail)
const isRecommendationDialogVisible = ref(false);
const sectorAttributionLoaded = ref(false);



onMounted(() => {
  // Load portfolio analytics data if not already loaded
  if (!store.portfolioAnalyticsData) {
    store.getPortfolioAnalyticsData();
  }
  formatNiftyDataForDonut();
  formatRiskAdjustedCharts();
  formatPerformanceCharts();

  // Check if sector attribution data is already available
  checkSectorAttributionAvailability();
});

// Toggle sector classification
const toggleSectorClassification = () => {
  isSectorClassificationOpen.value = !isSectorClassificationOpen.value;
};

// Set sector section (same pattern as SetGoals)
const setSectorSection = (section) => {
  activeSectorSection.value = section;
};

// Handle sector attribution loaded event
const handleSectorAttributionLoaded = () => {
  sectorAttributionLoaded.value = true;
};

// Check if sector attribution data is already available
const checkSectorAttributionAvailability = () => {
  // Check if portfolio analytics data exists and has the necessary structure
  if (portfolioAnalytics.value) {
    // Check for any of the key data structures
    const hasSectorData = portfolioAnalytics.value.SectorExposure;
    const hasPortfolioData = portfolioAnalytics.value.PortfolioData;
    const hasAllocationData = portfolioAnalytics.value.CurrentAssetAllocation;
    const hasOverviewData = portfolioAnalytics.value.PortfolioOverview;
    const hasReturnsData = portfolioAnalytics.value.Returns;

    if (hasSectorData || hasPortfolioData || hasAllocationData || hasOverviewData || hasReturnsData) {
      console.log('Portfolio data available, enabling recommendation button');
      sectorAttributionLoaded.value = true;
      return;
    }
  }

  // Also check store data directly
  if (store.portfolioAnalyticsData) {
    console.log('Portfolio analytics data found in store, enabling recommendation button');
    sectorAttributionLoaded.value = true;
    return;
  }

  // Additional checks for goal summary and financial statement
  if (store.goalSummary || store.financialStatement) {
    console.log('Goal summary or financial statement available');
    // If we have portfolio analytics data too, enable the button
    if (store.portfolioAnalyticsData) {
      sectorAttributionLoaded.value = true;
      return;
    }
  }

  console.log('Recommendation button conditions not met yet');
};

// Show recommendation dialog
const showRecommendationDialog = () => {
  isRecommendationDialogVisible.value = true;
};

// Hide recommendation dialog
const hideRecommendationDialog = () => {
  isRecommendationDialogVisible.value = false;
};

// Format Nifty50 sector data for donut chart
const formatNiftyDataForDonut = () => {
  if (!portfolioAnalytics.value?.Nifty50SectorWeights) return;

  const sectorWeights = portfolioAnalytics.value.Nifty50SectorWeights.SectorWeights;
  const labels = sectorWeights.map(item => item.Sector);
  const data = sectorWeights.map(item => parseFloat(item.Weight.replace('%', '')));

  niftyChartData.value = {
    labels,
    datasets: [
      {
        data,
        backgroundColor: chartColors.slice(0, labels.length),
      },
    ],
  };

  // Custom donut options for Nifty chart
  niftyDonutOptions.value = {
    cutout: "70%",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (tooltipItem) => {
            let value = tooltipItem.raw;
            return ` ${value}%`;
          },
        },
      },
    },
  };
};

// Calculate net worth from various investments
const calculateNetWorth = () => {
  if (!portfolioAnalytics.value) return 0;

  const data = portfolioAnalytics.value;
  let netWorth = 0;

  // Add stock investments
  if (data.Returns?.stocks) {
    netWorth += data.Returns.stocks.reduce((sum, stock) => {
      return sum + (stock.CMP * stock.NumberOfShares);
    }, 0);
  }

  // Add mutual fund investments
  if (data.Returns?.mutual_funds) {
    netWorth += data.Returns.mutual_funds.reduce((sum, fund) => {
      return sum + (fund.NAV * fund.NumberOfUnits);
    }, 0);
  }

  // Add fixed income current values
  if (data.Returns?.fixed_income) {
    netWorth += data.Returns.fixed_income.reduce((sum, investment) => {
      return sum + investment.CurrentValue;
    }, 0);
  }

  // Add other investments
  if (data.PortfolioOverview?.other_investments) {
    netWorth += data.PortfolioOverview.other_investments.reduce((sum, investment) => {
      return sum + investment.AmountInvested;
    }, 0);
  }

  return netWorth;
};

const formatCurrency = (amount) => {
  if (amount >= 10000000) {
    return `₹${(amount / 10000000).toFixed(1)} Cr`;
  } else if (amount >= 100000) {
    return `₹${(amount / 100000).toFixed(1)} L`;
  }
  return `₹${amount?.toLocaleString('en-IN') || 0}`;
};

const formatPercentage = (value) => {
  return `${value?.toFixed(2) || 0}%`;
};

// Calculate total investments for allocation percentages
const calculateTotalInvestments = () => {
  if (!portfolioAnalytics.value) return 0;

  const data = portfolioAnalytics.value;
  let total = 0;

  if (data.PortfolioOverview?.stocks) {
    total += data.PortfolioOverview.stocks.reduce((sum, stock) => {
      return sum + (stock.AverageBuyPrice * stock.NumberOfShares);
    }, 0);
  }

  if (data.PortfolioOverview?.mutual_funds) {
    total += data.PortfolioOverview.mutual_funds.reduce((sum, fund) => {
      return sum + fund.AmountInvested;
    }, 0);
  }

  if (data.PortfolioOverview?.fixed_income) {
    total += data.PortfolioOverview.fixed_income.reduce((sum, investment) => {
      return sum + investment.AmountInvested;
    }, 0);
  }

  if (data.PortfolioOverview?.other_investments) {
    total += data.PortfolioOverview.other_investments.reduce((sum, investment) => {
      return sum + investment.AmountInvested;
    }, 0);
  }

  return total;
};

const getSectorColor = (index) => {
  return chartColors[index % chartColors.length];
};

// Helper functions for sector analysis
const getTotalSectorCount = () => {
  if (!portfolioAnalytics.value?.Nifty50SectorWeights) return 11;
  return portfolioAnalytics.value.Nifty50SectorWeights.SectorWeights.length;
};

const getTop6Sectors = () => {
  if (!portfolioAnalytics.value?.Nifty50SectorWeights) {
    return [
      { sector: 'Information Technology', weight: '23.97%' },
      { sector: 'Financial Services', weight: '16.85%' },
      { sector: 'Consumer Goods', weight: '12.43%' },
      { sector: 'Automobiles', weight: '8.92%' },
      { sector: 'Healthcare', weight: '7.68%' },
      { sector: 'Energy', weight: '6.25%' }
    ];
  }

  const sectorWeights = portfolioAnalytics.value.Nifty50SectorWeights.SectorWeights;
  return sectorWeights
    .map(item => ({
      sector: item.Sector,
      weight: item.Weight
    }))
    .sort((a, b) => parseFloat(b.weight.replace('%', '')) - parseFloat(a.weight.replace('%', '')))
    .slice(0, 6);
};

const getLargestSectorInfo = () => {
  if (!portfolioAnalytics.value?.SectorExposure) {
    return { name: 'Information Technology', percentage: '22.29%' };
  }

  const sectors = portfolioAnalytics.value.SectorExposure;
  const maxSector = Object.keys(sectors).reduce((a, b) => sectors[a] > sectors[b] ? a : b);
  return {
    name: maxSector,
    percentage: `${sectors[maxSector].toFixed(2)}%`
  };
};

// Get Nifty 50 weight for the largest portfolio sector
const getNiftySectorWeight = () => {
  if (!portfolioAnalytics.value?.Nifty50SectorWeights) return '23.97';

  const largestSector = getLargestSectorInfo().name;
  const sectorWeights = portfolioAnalytics.value.Nifty50SectorWeights.SectorWeights;

  // Find matching sector in Nifty data (handle potential name variations)
  const niftySector = sectorWeights.find(sector =>
    sector.Sector.toLowerCase().includes(largestSector.toLowerCase()) ||
    largestSector.toLowerCase().includes(sector.Sector.toLowerCase())
  );

  return niftySector ? parseFloat(niftySector.Weight.replace('%', '')).toFixed(2) : '23.97';
};

// Calculate difference between portfolio and Nifty sector weight
const getSectorWeightDifference = () => {
  const portfolioWeight = parseFloat(getLargestSectorInfo().percentage.replace('%', ''));
  const niftyWeight = parseFloat(getNiftySectorWeight());
  const difference = niftyWeight - portfolioWeight; // Nifty 50 - Portfolio (as per requirement)

  // Apply the display logic
  if (difference < -5) {
    return 'Overweight';
  } else if (difference > 5) {
    return 'Underweight';
  } else {
    return '✓'; // Green tick mark for within +5% and -5%
  }
};

// Get CSS class for difference styling
const getDifferenceClass = () => {
  const portfolioWeight = parseFloat(getLargestSectorInfo().percentage.replace('%', ''));
  const niftyWeight = parseFloat(getNiftySectorWeight());
  const difference = niftyWeight - portfolioWeight; // Nifty 50 - Portfolio

  if (difference < -5) {
    return 'text-error font-weight-700'; // Red bold for Overweight
  } else if (difference > 5) {
    return 'text-error font-weight-700'; // Red bold for Underweight
  } else {
    return 'text-success font-weight-700'; // Green for tick mark (within range)
  }
};

// Get other sectors for collapsible section (original function)

// Get other sectors for collapsible section
const getOtherSectors = () => {
  if (!portfolioAnalytics.value?.Nifty50SectorWeights) {
    return [
      {
        name: 'Others',
        subcategories: ['Airlines', 'Building Materials', 'Cement', 'Diversified', 'Real Estate']
      }
    ];
  }

  const sectorWeights = portfolioAnalytics.value.Nifty50SectorWeights.SectorWeights;
  return sectorWeights
    .filter(sector => sector.SubCategories)
    .map(sector => ({
      name: sector.Sector,
      subcategories: sector.SubCategories
    }));
};

// Performance metrics chart data
const sharpeChartData = ref({
  labels: [],
  datasets: [{ data: [], borderColor: 'var(--an-success)', backgroundColor: 'var(--an-success)', tension: 0.4, fill: false, borderWidth: 2 }]
});
const sortinoChartData = ref({
  labels: [],
  datasets: [{ data: [], borderColor: 'var(--an-primary)', backgroundColor: 'var(--an-primary)', tension: 0.4, fill: false, borderWidth: 2 }]
});
const avgReturnChartData = ref({
  labels: [],
  datasets: [{ data: [], borderColor: 'var(--an-warning)', backgroundColor: 'var(--an-warning)', tension: 0.4, fill: false, borderWidth: 2 }]
});
const twrrChartData = ref({
  labels: [],
  datasets: [{ data: [], borderColor: 'var(--an-info)', backgroundColor: 'var(--an-info)', tension: 0.4, fill: false, borderWidth: 2 }]
});

// Mini chart options for performance cards
const getMiniChartOptions = (latestLabel = '') => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    intersect: false,
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: false,
    }
  },
  layout: {
    padding: {
      top: 20, // Add padding for data labels
    }
  },
  animation: {
    onComplete: function() {
      const ctx = this.ctx;
      const chart = this;

      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillStyle = '#333333';
      ctx.font = 'bold 12px Arial';

      chart.data.datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex);

        meta.data.forEach((point, index) => {
          const value = dataset.data[index];
          let displayValue;

          // Format value based on type
          if (typeof value === 'number') {
            if (Math.abs(value) < 1 && Math.abs(value) > 0.001) {
              displayValue = value.toFixed(3); // For Sharpe ratio
            } else if (Math.abs(value) > 100) {
              displayValue = value.toFixed(0); // For large Sortino values
            } else if (Math.abs(value) < 0.1) {
              displayValue = (value * 100).toFixed(1) + '%'; // For small percentages
            } else {
              displayValue = value.toFixed(2); // Default
            }
          } else {
            displayValue = value;
          }

          // Draw background for better readability
          const textWidth = ctx.measureText(displayValue).width;
          ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
          ctx.fillRect(point.x - textWidth/2 - 3, point.y - 18, textWidth + 6, 15);

          // Draw text
          ctx.fillStyle = '#333333';
          ctx.fillText(displayValue, point.x, point.y - 6);
        });
      });
    }
  },
  elements: {
    point: {
      radius: function(context) {
        const index = context.dataIndex;
        const totalPoints = context.dataset.data.length;
        return index === totalPoints - 1 ? 4 : 2; // Larger point for latest value
      },
      hoverRadius: 6,
      backgroundColor: function(context) {
        const index = context.dataIndex;
        const totalPoints = context.dataset.data.length;
        return index === totalPoints - 1 ? '#ffffff' : context.dataset.borderColor; // White center for latest
      },
      borderWidth: function(context) {
        const index = context.dataIndex;
        const totalPoints = context.dataset.data.length;
        return index === totalPoints - 1 ? 2 : 0; // Border for latest point
      },
    },
  },
  scales: {
    x: {
      display: true,
      grid: {
        display: false,
      },
      ticks: {
        font: {
          size: 10,
        },
        maxTicksLimit: 3,
        callback: function(value, index) {
          const totalTicks = this.getLabelForValue ? this.chart.data.labels.length : 0;
          // Show first, middle, and last labels
          if (index === 0 || index === totalTicks - 1 || index === Math.floor(totalTicks / 2)) {
            return this.getLabelForValue(value);
          }
          return '';
        }
      },
    },
    y: {
      display: true,
      grid: {
        display: true,
        color: '#f0f0f0',
      },
      ticks: {
        font: {
          size: 10,
        },
        maxTicksLimit: 4,
      },
    },
  },
});

// Helper function to determine trend color based on data values
const getTrendColor = (dataArray) => {
  if (!dataArray || dataArray.length < 2) return '#28a745'; // Default green

  const firstValue = dataArray[0];
  const lastValue = dataArray[dataArray.length - 1];

  // If trend is increasing (last value > first value), return green
  // If trend is decreasing (last value < first value), return red
  return lastValue >= firstValue ? '#28a745' : '#dc3545';
};

// Format risk-adjusted metrics data for line charts (Sharpe & Sortino only)
const formatRiskAdjustedCharts = () => {
  if (!portfolioAnalytics.value?.MonthlyRiskReturnMetrics || !Array.isArray(portfolioAnalytics.value.MonthlyRiskReturnMetrics)) return;

  const metrics = portfolioAnalytics.value.MonthlyRiskReturnMetrics;
  if (metrics.length === 0) return;

  // Sort metrics by date (oldest first) and format labels as MM-YYYY
  const sortedMetrics = [...metrics].sort((a, b) => new Date(a.month) - new Date(b.month));
  const labels = sortedMetrics.map(m => {
    if (m.month) {
      const date = new Date(m.month);
      return `${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
    }
    return m.month;
  });

  // Prepare data arrays for risk-adjusted metrics only
  const sharpeData = sortedMetrics.map(m => m.sharpe_ratio);
  const sortinoData = sortedMetrics.map(m => m.sortino_ratio);

  // Sharpe Ratio Chart
  sharpeChartData.value = {
    labels,
    datasets: [{
      data: sharpeData,
      borderColor: getTrendColor(sharpeData),
      backgroundColor: getTrendColor(sharpeData),
      tension: 0.4,
      fill: false,
      borderWidth: 3,
      pointBackgroundColor: sharpeData.map((_, index) =>
        index === sharpeData.length - 1 ? '#ffffff' : getTrendColor(sharpeData)
      ),
      pointBorderColor: getTrendColor(sharpeData),
      pointBorderWidth: sharpeData.map((_, index) =>
        index === sharpeData.length - 1 ? 2 : 0
      ),
      pointRadius: sharpeData.map((_, index) =>
        index === sharpeData.length - 1 ? 4 : 2
      ),
    }]
  };

  // Sortino Ratio Chart
  sortinoChartData.value = {
    labels,
    datasets: [{
      data: sortinoData,
      borderColor: getTrendColor(sortinoData),
      backgroundColor: getTrendColor(sortinoData),
      tension: 0.4,
      fill: false,
      borderWidth: 3,
      pointBackgroundColor: sortinoData.map((_, index) =>
        index === sortinoData.length - 1 ? '#ffffff' : getTrendColor(sortinoData)
      ),
      pointBorderColor: getTrendColor(sortinoData),
      pointBorderWidth: sortinoData.map((_, index) =>
        index === sortinoData.length - 1 ? 2 : 0
      ),
      pointRadius: sortinoData.map((_, index) =>
        index === sortinoData.length - 1 ? 4 : 2
      ),
    }]
  };
};

// Format performance metrics data for line charts (TWRR & Average Return only)
const formatPerformanceCharts = () => {
  if (!portfolioAnalytics.value?.MonthlyRiskReturnMetrics || !Array.isArray(portfolioAnalytics.value.MonthlyRiskReturnMetrics)) return;

  const metrics = portfolioAnalytics.value.MonthlyRiskReturnMetrics;
  if (metrics.length === 0) return;

  // Sort metrics by date (oldest first) and format labels as MM-YYYY
  const sortedMetrics = [...metrics].sort((a, b) => new Date(a.month) - new Date(b.month));
  const labels = sortedMetrics.map(m => {
    if (m.month) {
      const date = new Date(m.month);
      return `${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
    }
    return m.month;
  });

  // Prepare data arrays for performance metrics only
  const avgReturnData = sortedMetrics.map(m => m.avg_monthly_return);
  const twrrData = sortedMetrics.map(m => m.twrr_pct);

  // Average Monthly Return Chart
  avgReturnChartData.value = {
    labels,
    datasets: [{
      data: avgReturnData,
      borderColor: getTrendColor(avgReturnData),
      backgroundColor: getTrendColor(avgReturnData),
      tension: 0.4,
      fill: false,
      borderWidth: 3,
      pointBackgroundColor: avgReturnData.map((_, index) =>
        index === avgReturnData.length - 1 ? '#ffffff' : getTrendColor(avgReturnData)
      ),
      pointBorderColor: getTrendColor(avgReturnData),
      pointBorderWidth: avgReturnData.map((_, index) =>
        index === avgReturnData.length - 1 ? 2 : 0
      ),
      pointRadius: avgReturnData.map((_, index) =>
        index === avgReturnData.length - 1 ? 4 : 2
      ),
    }]
  };

  // TWRR Chart
  twrrChartData.value = {
    labels,
    datasets: [{
      data: twrrData,
      borderColor: getTrendColor(twrrData),
      backgroundColor: getTrendColor(twrrData),
      tension: 0.4,
      fill: false,
      borderWidth: 3,
      pointBackgroundColor: twrrData.map((_, index) =>
        index === twrrData.length - 1 ? '#ffffff' : getTrendColor(twrrData)
      ),
      pointBorderColor: getTrendColor(twrrData),
      pointBorderWidth: twrrData.map((_, index) =>
        index === twrrData.length - 1 ? 2 : 0
      ),
      pointRadius: twrrData.map((_, index) =>
        index === twrrData.length - 1 ? 4 : 2
      ),
    }]
  };
};

// Sector analysis functions using SectorExposure
const getTopContributorSector = () => {
  if (!portfolioAnalytics.value?.SectorExposure) return 'No Data';
  const sectors = portfolioAnalytics.value.SectorExposure;

  // Find sector with highest exposure (contributor)
  const maxSector = Object.keys(sectors).reduce((a, b) => sectors[a] > sectors[b] ? a : b);
  return maxSector;
};

const getContributorValue = () => {
  if (!portfolioAnalytics.value?.SectorExposure) return '0.00';
  const sectors = portfolioAnalytics.value.SectorExposure;

  const maxValue = Math.max(...Object.values(sectors));
  return maxValue?.toFixed(2) || '0.00';
};

const getTopDetractorSector = () => {
  if (!portfolioAnalytics.value?.SectorExposure) return 'No Data';
  const sectors = portfolioAnalytics.value.SectorExposure;

  // Find sector with lowest exposure (detractor)
  const minSector = Object.keys(sectors).reduce((a, b) => sectors[a] < sectors[b] ? a : b);
  return minSector;
};

const getDetractorValue = () => {
  if (!portfolioAnalytics.value?.SectorExposure) return '0.00';
  const sectors = portfolioAnalytics.value.SectorExposure;

  const minValue = Math.min(...Object.values(sectors));
  return Math.abs(minValue)?.toFixed(2) || '0.00';
};

const getLargestOverweightSector = () => {
  if (!portfolioAnalytics.value?.SectorExposure) return 'No Data';
  const sectors = portfolioAnalytics.value.SectorExposure;

  // Find sector with highest exposure for overweight
  const maxSector = Object.keys(sectors).reduce((a, b) => sectors[a] > sectors[b] ? a : b);
  return maxSector;
};

const getLargestOverweight = () => {
  if (!portfolioAnalytics.value?.SectorExposure) return { percentage: '0.00', fund: '0.00', benchmark: '0.00' };
  const sectors = portfolioAnalytics.value.SectorExposure;

  const maxSector = getLargestOverweightSector();
  const maxValue = sectors[maxSector];

  return {
    percentage: (maxValue * 0.15).toFixed(2), // Mock overweight calculation
    fund: maxValue?.toFixed(2) || '0.00',
    benchmark: (maxValue * 0.85).toFixed(2) // Mock benchmark weight
  };
};

const getLargestUnderweightSector = () => {
  if (!portfolioAnalytics.value?.SectorExposure) return 'No Data';
  const sectors = portfolioAnalytics.value.SectorExposure;

  // Find sector with lowest exposure for underweight
  const minSector = Object.keys(sectors).reduce((a, b) => sectors[a] < sectors[b] ? a : b);
  return minSector;
};

const getLargestUnderweight = () => {
  if (!portfolioAnalytics.value?.SectorExposure) return { percentage: '0.00', fund: '0.00', benchmark: '0.00' };
  const sectors = portfolioAnalytics.value.SectorExposure;

  const minSector = getLargestUnderweightSector();
  const minValue = sectors[minSector];

  return {
    percentage: Math.abs(minValue * -0.15).toFixed(2), // Mock underweight calculation
    fund: minValue?.toFixed(2) || '0.00',
    benchmark: (minValue * 1.15).toFixed(2) // Mock benchmark weight
  };
};

// Get current values for performance metrics
const getCurrentSharpe = () => {
  if (!portfolioAnalytics.value?.MonthlyRiskReturnMetrics || !Array.isArray(portfolioAnalytics.value.MonthlyRiskReturnMetrics) || portfolioAnalytics.value.MonthlyRiskReturnMetrics.length === 0) return null;
  return portfolioAnalytics.value.MonthlyRiskReturnMetrics[0].sharpe_ratio.toFixed(3);
};

const getCurrentSortino = () => {
  if (!portfolioAnalytics.value?.MonthlyRiskReturnMetrics || !Array.isArray(portfolioAnalytics.value.MonthlyRiskReturnMetrics) || portfolioAnalytics.value.MonthlyRiskReturnMetrics.length === 0) return null;
  return portfolioAnalytics.value.MonthlyRiskReturnMetrics[0].sortino_ratio.toFixed(2);
};

const getCurrentAvgReturn = () => {
  if (!portfolioAnalytics.value?.MonthlyRiskReturnMetrics || !Array.isArray(portfolioAnalytics.value.MonthlyRiskReturnMetrics) || portfolioAnalytics.value.MonthlyRiskReturnMetrics.length === 0) return null;
  return `${(portfolioAnalytics.value.MonthlyRiskReturnMetrics[0].avg_monthly_return * 100).toFixed(1)}%`;
};

const getCurrentTWRR = () => {
  if (!portfolioAnalytics.value?.MonthlyRiskReturnMetrics || !Array.isArray(portfolioAnalytics.value.MonthlyRiskReturnMetrics) || portfolioAnalytics.value.MonthlyRiskReturnMetrics.length === 0) return null;
  return `${portfolioAnalytics.value.MonthlyRiskReturnMetrics[0].twrr_pct.toFixed(2)}%`;
};

// Check if performance metrics are available
const hasPerformanceMetrics = computed(() => {
  return portfolioAnalytics.value?.MonthlyRiskReturnMetrics &&
         Array.isArray(portfolioAnalytics.value.MonthlyRiskReturnMetrics) &&
         portfolioAnalytics.value.MonthlyRiskReturnMetrics.length > 0;
});



// Get current date formatted as MMM-YYYY
const getCurrentDate = computed(() => {
  const today = new Date();
  const month = today.toLocaleDateString('en-US', { month: 'short' });
  const year = today.getFullYear();
  return `${month}-${year}`;
});

// Portfolio Overview helper functions (Financial Portfolio - excludes Gold & Real Estate)
const getPortfolioOverview = () => {
  if (!portfolioAnalytics.value?.PortfolioOverview) {
    // Fallback mock data when backend data is not available
    // These are placeholder values for UI development/testing only
    return {
      BeginningPortfolioValue: 0,
      Contributions: 0,
      Withdrawals: 0,
      InterestAndDividends: 0,
      EndingPortfolioValue: 0,
      CapitalAppreciation: 0,
      TotalInvestmentGain: 0,
      IsNewlyOnboarded: false,
      DataAvailableFrom: null
    };
  }
  return portfolioAnalytics.value.PortfolioOverview;
};

const getPortfolioGainPercentage = () => {
  const overview = getPortfolioOverview();
  if (overview.BeginningPortfolioValue === 0) return '0.00';
  const gainPercentage = (overview.TotalInvestmentGain / overview.BeginningPortfolioValue) * 100;
  return gainPercentage.toFixed(2);
};

// Check if user is newly onboarded
const isNewlyOnboarded = computed(() => {
  const overview = getPortfolioOverview();
  return overview.IsNewlyOnboarded === true;
});

// Format data available from date
const formatDataAvailableFrom = () => {
  const overview = getPortfolioOverview();
  if (!overview.DataAvailableFrom) return '';
  const date = new Date(overview.DataAvailableFrom);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Asset Allocation helper functions
const getCurrentAssetAllocation = () => {
  return portfolioAnalytics.value?.CurrentAssetAllocation || [];
};

const getTargetAssetAllocation = () => {
  return portfolioAnalytics.value?.TargetAssetAllocation || [];
};

// Calculate total net worth (includes all asset classes including Gold & Real Estate)
const calculateTotalNetWorth = () => {
  const currentAllocation = getCurrentAssetAllocation();
  return currentAllocation.reduce((total, asset) => total + asset.Market_Value, 0);
};

// Get combined asset allocation data for comparison table (existing assets only)
const getAssetAllocationComparison = () => {
  const current = getCurrentAssetAllocation();
  const target = getTargetAssetAllocation();

  // Create a map of target allocations by asset class name
  const targetMap = {};
  target.forEach(asset => {
    targetMap[asset.AssetClassName] = asset.TargetPct;
  });

  return current.map(asset => {
    const targetPct = targetMap[asset.AssetClassName] || 0;
    const deviation = asset.Current_Pct - targetPct;

    return {
      AssetClassName: asset.AssetClassName,
      Current_Pct: asset.Current_Pct,
      Target_Pct: targetPct,
      Market_Value: asset.Market_Value,
      Deviation: deviation,
      Status: Math.abs(deviation) <= 2 ? 'On Track' :
              deviation > 2 ? 'Overweight' : 'Underweight'
    };
  }).sort((a, b) => b.Market_Value - a.Market_Value); // Sort by market value descending
};

// Get missing asset classes (target exists but not in current portfolio)
const getMissingAssetClasses = () => {
  const current = getCurrentAssetAllocation();
  const target = getTargetAssetAllocation();

  // Create a map of current allocations by asset class name
  const currentMap = {};
  current.forEach(asset => {
    currentMap[asset.AssetClassName] = asset;
  });

  // Find target assets that don't exist in current portfolio
  return target
    .filter(targetAsset => !currentMap[targetAsset.AssetClassName])
    .map(targetAsset => ({
      AssetClassName: targetAsset.AssetClassName,
      Target_Pct: targetAsset.TargetPct
    }))
    .sort((a, b) => b.Target_Pct - a.Target_Pct); // Sort by target percentage descending
};// Get deviation status class for styling
const getDeviationClass = (deviation) => {
  if (Math.abs(deviation) <= 2) return 'text-success';
  if (deviation > 2) return 'text-warning';
  return 'text-error';
};

// Get status badge class
const getStatusBadgeClass = (status) => {
  switch (status) {
    case 'On Track': return 'status-badge-success';
    case 'Overweight': return 'status-badge-warning';
    case 'Underweight': return 'status-badge-error';
    default: return 'status-badge-neutral';
  }
};

// Watch for portfolio data changes and regenerate ALL chart data
watch(portfolioAnalytics, (newValue) => {
  if (newValue) {
    formatNiftyDataForDonut();
    formatRiskAdjustedCharts();
    formatPerformanceCharts();
    // Check if button should be enabled when data becomes available
    checkSectorAttributionAvailability();
  }
}, { immediate: true });

// Watch for store portfolio analytics data changes
watch(() => store.portfolioAnalyticsData, (newValue) => {
  if (newValue) {
    console.log('Store portfolio analytics data updated, checking button availability');
    checkSectorAttributionAvailability();
  }
}, { immediate: true });

// Watch for store loading state changes
watch(() => store.isDashboardLoading, (newValue, oldValue) => {
  if (oldValue && !newValue) {
    // Loading just finished, check if button should be enabled
    console.log('Dashboard loading finished, checking button availability');
    checkSectorAttributionAvailability();
  }
}, { immediate: true });
</script>

<template>
  <div>
    <AnLoader v-if="store.isDashboardLoading" text="Loading Portfolio Analytics..." />
    <div v-else-if="!portfolioAnalytics">There is some issue</div>

    <!-- Section 1: Full Portfolio Analytics Page -->
    <div v-else-if="activeSectorSection === 1" class="portfolio-analytics-page">
      <!-- Sector Analysis Section -->
      <div class="sector-analysis-container input-shadow br-10 p-20 mt-30">
        <!-- Portfolio Snapshot Row -->
        <div class="portfolio-snapshot-container mb-30">
        <div class="perf-card portfolio-snapshot-card">
          <div class="card-header">
            <div class="card-header-content">
              <span class="card-title">Financial Portfolio Snapshot</span>
              <span class="card-subtitle text-muted">Stocks, Mutual Funds & Fixed Income</span>
              <span class="card-date text-muted">for {{ getCurrentDate }}</span>
            </div>
            <!-- Recommendation Button (only show when sector attribution is loaded) -->

          </div>
          <div class="card-content">
            <!-- Newly Onboarded User Message -->
            <div v-if="isNewlyOnboarded" class="newly-onboarded-message">
              <div class="onboarded-icon">🎉</div>
              <div class="onboarded-content">
                <h4 class="onboarded-title">Welcome to Your Portfolio Analytics!</h4>
                <p class="onboarded-text">
                  You've recently joined us{{ formatDataAvailableFrom() ? ` on ${formatDataAvailableFrom()}` : '' }}.
                  We're building your portfolio performance history and will have detailed insights available soon.
                </p>
                <div class="onboarded-current-value">
                  <span class="current-value-label">Current Portfolio Value:</span>
                  <span class="current-value-amount text-primary">{{ formatCurrency(getPortfolioOverview().EndingPortfolioValue) }}</span>
                </div>
                <p class="onboarded-note">
                  Check back after your first full month to see performance metrics, attribution analysis, and personalized recommendations.
                </p>
              </div>
            </div>

            <!-- Regular Portfolio Overview (Existing Users) -->
            <div v-else class="portfolio-overview-horizontal">
              <div class="portfolio-metric-col">
                <span class="metric-label-small">Beginning Value</span>
                <span class="metric-value-medium">{{ formatCurrency(getPortfolioOverview().BeginningPortfolioValue) }}</span>
              </div>
              <div class="portfolio-metric-col" v-if="getPortfolioOverview().Contributions > 0">
                <div class="metric-label-with-tooltip">
                  <span class="metric-label-small">Contributions</span>
                  <div class="tooltip-container">
                    <span class="info-icon">ℹ️</span>
                    <div class="tooltip-content portfolio-tooltip">
                      <strong>Contributions:</strong> Total amount added to the financial portfolio during the selected period. Includes: Contribution, Deposit, Buy, TransferIn
                    </div>
                  </div>
                </div>
                <span class="metric-value-small text-success">+{{ formatCurrency(getPortfolioOverview().Contributions) }}</span>
              </div>
              <div class="portfolio-metric-col" v-if="getPortfolioOverview().Withdrawals > 0">
                <div class="metric-label-with-tooltip">
                  <span class="metric-label-small">Withdrawals</span>
                  <div class="tooltip-container">
                    <span class="info-icon">ℹ️</span>
                    <div class="tooltip-content portfolio-tooltip">
                      <strong>Withdrawals:</strong> Total amount taken out of the financial portfolio during the selected period. Includes: Withdrawal, Redemption, Sell, TransferOut
                    </div>
                  </div>
                </div>
                <span class="metric-value-small text-error">-{{ formatCurrency(getPortfolioOverview().Withdrawals) }}</span>
              </div>
              <div class="portfolio-metric-col" v-if="getPortfolioOverview().InterestAndDividends > 0">
                <div class="metric-label-with-tooltip">
                  <span class="metric-label-small">Interest & Dividends</span>
                  <div class="tooltip-container">
                    <span class="info-icon">ℹ️</span>
                    <div class="tooltip-content portfolio-tooltip">
                      <strong>Interest & Dividends:</strong> Income earned from investments during the selected period. Includes: Interest, Dividend, Coupon, Payout, Distribution
                    </div>
                  </div>
                </div>
                <span class="metric-value-small text-info">+{{ formatCurrency(getPortfolioOverview().InterestAndDividends) }}</span>
              </div>
              <div class="portfolio-metric-col">
                <span class="metric-label-small">Capital Appreciation</span>
                <span class="metric-value-medium" :class="getPortfolioOverview().CapitalAppreciation >= 0 ? 'text-success' : 'text-error'">{{ formatCurrency(getPortfolioOverview().CapitalAppreciation) }}</span>
              </div>
              <div class="portfolio-metric-col">
                <span class="metric-label-small">Ending Value</span>
                <span class="metric-value-large text-primary">{{ formatCurrency(getPortfolioOverview().EndingPortfolioValue) }}</span>
              </div>
              <div class="portfolio-metric-col">
                <span class="metric-label-small">Total Gain</span>
                <span class="metric-value-medium" :class="getPortfolioOverview().TotalInvestmentGain >= 0 ? 'text-success' : 'text-error'">{{ formatCurrency(getPortfolioOverview().TotalInvestmentGain) }} ({{ getPortfolioOverview().TotalInvestmentGain >= 0 ? '+' : '' }}{{ getPortfolioGainPercentage() }}%)</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Recommendation Button (only show when sector attribution is loaded) -->
        <div v-if="sectorAttributionLoaded" class="recommendation-button-section">
          <button class="recommendation-button" @click="showRecommendationDialog">
            <span class="button-icon">🎯</span>
            <span class="button-text">Generate Portfolio Recommendations</span>
            <span class="button-arrow">→</span>
          </button>
        </div>
      </div>

      <!-- Asset Allocation Section -->
      <div class="asset-allocation-container mb-30">
        <div class="perf-card asset-allocation-card">
          <div class="card-header">
            <div class="card-header-content">
              <span class="card-title">Asset Allocation vs Target</span>
              <span class="card-subtitle text-muted">Total Net Worth: {{ formatCurrency(calculateTotalNetWorth()) }}</span>
            </div>
            <div class="card-tabs">
              <button
                class="tab-button"
                :class="{ 'tab-active': activeAllocationTab === 'table' }"
                @click="activeAllocationTab = 'table'"
              >
                Table View
              </button>
              <button
                class="tab-button"
                :class="{ 'tab-active': activeAllocationTab === 'chart' }"
                @click="activeAllocationTab = 'chart'"
              >
                Chart View
              </button>
            </div>
          </div>

          <div class="card-content">
            <!-- Table Tab Content -->
            <div v-if="activeAllocationTab === 'table'" class="tab-content">
              <!-- Main Asset Allocation Table (Existing Assets Only) -->
              <div class="asset-allocation-table">
                <div class="allocation-table-header">
                  <div class="col-asset-name">Asset Class</div>
                  <div class="col-value">Market Value</div>
                  <div class="col-current">Current</div>
                  <div class="col-target">Target</div>
                  <div class="col-status status-header">
                    <div class="status-sub-header">Deviation</div>
                    <div class="status-sub-header">Status</div>
                  </div>
                </div>
                <div class="allocation-table-body">
                  <div
                    v-for="asset in getAssetAllocationComparison()"
                    :key="asset.AssetClassName"
                    class="allocation-row"
                  >
                    <div class="col-asset-name">
                      <span class="asset-name-text">{{ asset.AssetClassName }}</span>
                    </div>
                    <div class="col-value">
                      <span class="value-text">{{ formatCurrency(asset.Market_Value) }}</span>
                    </div>
                    <div class="col-current">
                      <span class="percentage-text">{{ asset.Current_Pct.toFixed(1) }}%</span>
                    </div>
                    <div class="col-target">
                      <span class="percentage-text">{{ asset.Target_Pct.toFixed(1) }}%</span>
                    </div>
                    <div class="col-status">
                      <div class="status-deviation">
                        <span :class="[getDeviationClass(asset.Deviation), 'deviation-text']">
                          {{ asset.Deviation > 0 ? '+' : '' }}{{ asset.Deviation.toFixed(1) }}%
                        </span>
                      </div>
                      <div class="status-badge-container">
                        <span :class="getStatusBadgeClass(asset.Status)" class="status-badge">
                          {{ asset.Status }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Missing Asset Classes Collapsible Section -->
              <div v-if="getMissingAssetClasses().length > 0" class="missing-assets-section mt-20">
                <div class="collapsible-header" @click="isMissingAssetsOpen = !isMissingAssetsOpen">
                  <div class="collapsible-title-container">
                    <span class="collapsible-icon" :class="{ 'rotated': isMissingAssetsOpen }">▶</span>
                    <span class="collapsible-title">Missing Asset Classes</span>
                    <span class="missing-count-badge">{{ getMissingAssetClasses().length }}</span>
                  </div>
                  <span class="collapsible-subtitle">Target allocations not present in current portfolio</span>
                </div>

                <div v-show="isMissingAssetsOpen" class="collapsible-content">
                  <div class="missing-assets-grid">
                    <div
                      v-for="missingAsset in getMissingAssetClasses()"
                      :key="missingAsset.AssetClassName"
                      class="missing-asset-item"
                    >
                      <div class="missing-asset-name">
                        <span class="asset-name">{{ missingAsset.AssetClassName }}</span>
                        <span class="missing-indicator">⚠️</span>
                      </div>
                      <div class="suggested-allocation">
                        <span class="allocation-label">Suggested:</span>
                        <span class="allocation-value">{{ missingAsset.Target_Pct.toFixed(1) }}%</span>
                      </div>
                    </div>
                  </div>
                  <div class="missing-assets-note">
                    <span class="note-text">
                      Consider adding these asset classes to achieve your agreed diversification strategy and reduce portfolio risk.
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Chart Tab Content -->
            <div v-if="activeAllocationTab === 'chart'" class="tab-content">
              <AssetAllocationChart
                :currentAssetAllocation="getCurrentAssetAllocation()"
                :targetAssetAllocation="getTargetAssetAllocation()"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Sector Attribution Overview (Replaces old sector performance cards) -->
      <div class="sector-attribution-container mb-30">
        <SectorAttributionOverview
          :portfolio-id="store.dashboardData?.portfolioId"
          :portfolio-analytics="portfolioAnalytics"
          :setSection="setSectorSection"
          @attribution-loaded="handleSectorAttributionLoaded" />
      </div>

      <!-- Nifty 50 Chart and Comparison Section -->
      <div class="nifty-analysis-container mb-20">
        <div class="nifty-content-grid">
          <!-- Left: Nifty 50 Doughnut Chart -->
          <div class="nifty-chart-section bg-white br-10 p-20">
            <div class="nifty-header mb-8">
              <h4 class="nifty-header-title text-primary mb-2">Nifty 50 Sectors</h4>
            </div>

            <div class="nifty-chart-wrapper">
              <!-- Centered Doughnut Chart -->
              <div class="chart-container-centered">
                <Chart
                  type="doughnut"
                  :data="niftyChartData"
                  :options="niftyDonutOptions"
                  class="nifty-donut-chart"
                />
                <div class="chart-center-text">
                  <div class="center-line1">{{ getTotalSectorCount() }} sectors</div>
                </div>
              </div>

              <!-- Top 6 Sectors Grid -->
              <div class="top-sectors-grid-container">
                <h5 class="top-sectors-title">Top 6 Sectors by M-Cap Weightage</h5>
                <div class="sectors-grid">
                  <div
                    v-for="(sector, index) in getTop6Sectors()"
                    :key="sector.sector"
                    class="sector-box"
                  >
                    <div class="sector-box-header">
                      <div class="sector-dot" :style="{ backgroundColor: getSectorColor(index) }"></div>
                      <div class="sector-weight-badge">{{ sector.weight }}</div>
                    </div>
                    <div class="sector-box-content">
                      <span class="sector-name">{{ sector.sector }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Right: Sector Benchmark Charts -->
          <div class="comparison-section">
            <SectorBenchmarkChart
              :sector-exposure="portfolioAnalytics?.SectorExposure || {}"
            />
          </div>
        </div>
      </div>

      <!-- Performance Metrics Charts at the bottom -->
      <div class="performance-metrics-container">
        <!-- Performance Metrics Not Available Message -->
        <div v-if="!hasPerformanceMetrics" class="metrics-unavailable-message">
          <div class="unavailable-icon">📊</div>
          <div class="unavailable-content">
            <h4 class="unavailable-title">Performance Metrics Coming Soon</h4>
            <p class="unavailable-text">
              Risk-adjusted and performance metrics will be available after at least one full month of portfolio tracking in AssetNivesh.
            </p>
          </div>
        </div>

        <!-- Performance Metrics Charts (when data available) -->
        <div v-else>
        <!-- Risk-Adjusted Metrics Section -->
        <div class="metrics-section mb-25">
          <div class="metrics-header">
            <h3 class="metrics-section-title">Risk-Adjusted Metrics</h3>
            <span class="trend-indicator text-muted">3-Month Trend</span>
          </div>

          <!-- Single Tabbed Card for Risk-Adjusted Metrics -->
          <div class="perf-card combined-risk-card">
            <div class="card-header">
              <div class="card-tabs">
                <button
                  class="tab-button"
                  :class="{ 'tab-active': activeRiskTab === 'sharpe' }"
                  @click="activeRiskTab = 'sharpe'"
                >
                  Sharpe Ratio
                </button>
                <button
                  class="tab-button"
                  :class="{ 'tab-active': activeRiskTab === 'sortino' }"
                  @click="activeRiskTab = 'sortino'"
                >
                  Sortino Ratio
                </button>
              </div>
            </div>

            <div class="card-content">
              <!-- Sharpe Ratio Tab Content -->
              <div v-if="activeRiskTab === 'sharpe'" class="tab-content">
                <div class="card-title-container">
                  <span class="card-subtitle">Sharpe Ratio (Risk-Adjusted Performance)</span>
                  <div class="tooltip-container">
                    <span class="info-icon">ℹ️</span>
                    <div class="tooltip-content">
                      <strong>Sharpe Ratio:</strong> Measures how much excess return the portfolio earned per unit of total volatility.<br><br>
                      <strong>Formula:</strong> (Average return − Risk-free rate) ÷ Standard deviation of returns.<br><br>
                      <strong>Interpretation:</strong> Higher is better; values above 1 are generally considered good.
                    </div>
                  </div>
                </div>
                <div class="metric-display">
                  <span class="metric-label">Current Val</span>
                  <span class="metric-separator">:</span>
                  <span class="metric-value">{{ getCurrentSharpe() }}</span>
                </div>
                <div class="mini-chart">
                  <Chart
                    type="line"
                    :data="sharpeChartData"
                    :options="getMiniChartOptions()"
                    class="performance-mini-chart"
                  />
                </div>
              </div>

              <!-- Sortino Ratio Tab Content -->
              <div v-if="activeRiskTab === 'sortino'" class="tab-content">
                <div class="card-title-container">
                  <span class="card-subtitle">Sortino Ratio (Downside Risk-Adjusted Performance)</span>
                  <div class="tooltip-container">
                    <span class="info-icon">ℹ️</span>
                    <div class="tooltip-content">
                      <strong>Sortino Ratio:</strong> Similar to Sharpe but focuses only on downside risk.<br><br>
                      <strong>Formula:</strong> (Average return − Minimum acceptable return) ÷ Downside deviation.<br><br>
                      <strong>Interpretation:</strong> Higher is better; penalizes negative returns more than Sharpe.
                    </div>
                  </div>
                </div>
                <div class="metric-display">
                  <span class="metric-label">Current Val</span>
                  <span class="metric-separator">:</span>
                  <span class="metric-value">{{ getCurrentSortino() }}</span>
                </div>
                <div class="mini-chart">
                  <Chart
                    type="line"
                    :data="sortinoChartData"
                    :options="getMiniChartOptions()"
                    class="performance-mini-chart"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Performance Metrics Section -->
        <div class="metrics-section">
          <div class="metrics-header">
            <h3 class="metrics-section-title">Performance Metrics</h3>
            <span class="trend-indicator text-muted">3-Month Trend</span>
          </div>

          <!-- Single Tabbed Card for Performance Metrics -->
          <div class="perf-card combined-performance-card">
            <div class="card-header">
              <div class="card-tabs">
                <button
                  class="tab-button"
                  :class="{ 'tab-active': activePerformanceTab === 'twrr' }"
                  @click="activePerformanceTab = 'twrr'"
                >
                  Rolling 3-Month Return (Compounded)
                </button>
                <button
                  class="tab-button"
                  :class="{ 'tab-active': activePerformanceTab === 'avgReturn' }"
                  @click="activePerformanceTab = 'avgReturn'"
                >
                  Average Monthly Return
                </button>
              </div>
            </div>

            <div class="card-content">
              <!-- TWRR Tab Content -->
              <div v-if="activePerformanceTab === 'twrr'" class="tab-content">
                <div class="card-title-container">
                  <span class="card-subtitle">Rolling 3-Month Return (Compounded) - TWRR</span>
                  <div class="tooltip-container">
                    <span class="info-icon">ℹ️</span>
                    <div class="tooltip-content">
                      <strong>TWRR:</strong> Total compounded return for the last 3 months, adjusted for cash flows.<br><br>
                      <strong>Key Feature:</strong> Excludes impact of deposits/withdrawals to show true portfolio performance.<br><br>
                      <strong>Interpretation:</strong> Higher is better; compare with average return to see compounding effect.
                    </div>
                  </div>
                </div>
                <div class="metric-display">
                  <span class="metric-label">Current Val</span>
                  <span class="metric-separator">:</span>
                  <span class="metric-value">{{ getCurrentTWRR() }}</span>
                </div>
                <div class="mini-chart">
                  <Chart
                    type="line"
                    :data="twrrChartData"
                    :options="getMiniChartOptions()"
                    class="performance-mini-chart"
                  />
                </div>
              </div>

              <!-- Average Monthly Return Tab Content -->
              <div v-if="activePerformanceTab === 'avgReturn'" class="tab-content">
                <div class="card-title-container">
                  <span class="card-subtitle">Average Monthly Return (Last 3 Months)</span>
                  <div class="tooltip-container">
                    <span class="info-icon">ℹ️</span>
                    <div class="tooltip-content">
                      <strong>Average Monthly Return:</strong> Simple average of monthly returns over the last 3 months.<br><br>
                      <strong>Usage:</strong> Shows short-term trends without compounding effects.<br><br>
                      <strong>Interpretation:</strong> Higher is better; compare with TWRR to see compounding impact.
                    </div>
                  </div>
                </div>
                <div class="metric-display">
                  <span class="metric-label">Current Val</span>
                  <span class="metric-separator">:</span>
                  <span class="metric-value">{{ getCurrentAvgReturn() }}</span>
                </div>
                <div class="mini-chart">
                  <Chart
                    type="line"
                    :data="avgReturnChartData"
                    :options="getMiniChartOptions()"
                    class="performance-mini-chart"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Date Footer -->
        <div class="date-footer mt-20 text-right">
          <span class="text-muted">Data as at {{ getCurrentDate }}</span>
        </div>
        </div>
      </div>
      </div>
    </div> <!-- End Section 1: Portfolio Analytics -->

    <!-- Section 2: Sector Analytics Detail Page -->
    <div v-else-if="activeSectorSection === 2" class="sector-analytics-detail-page">
      <SectorAnalyticsDetail :setSection="setSectorSection" />
    </div>

    <!-- Generate Recommendation Summary Dialog -->
    <GenerateRecommendationSummary
      :is-visible="isRecommendationDialogVisible"
      @close="hideRecommendationDialog"
    />
  </div>
</template>



<style scoped>
/* Sector Analysis Container */
.sector-analysis-container {
  max-width: 100%;
}

/* Header Section */
.header-section {
  text-align: center;
  margin-bottom: 40px;
  margin-top: 0;
}

.main-headline {
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--an-primary) 0%, #4A90E2 50%, #7B68EE 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 10px;
  margin-top: 0;
  line-height: 1.0;
  letter-spacing: -0.5px;
}

.main-headline::after {
  content: '';
  display: block;
  width: 80px;
  height: 3px;
  background: linear-gradient(135deg, var(--an-primary), #4A90E2);
  margin: 10px auto 0;
  border-radius: 2px;
}

/* Portfolio Snapshot Container */
.portfolio-snapshot-container {
  width: 100%;
  margin-bottom: 20px;
}

/* Newly Onboarded User Message */
.newly-onboarded-message {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 24px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 2px solid #bae6fd;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.1);
}

/* Performance Metrics Unavailable Message */
.metrics-unavailable-message {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 30px;
  background: linear-gradient(135deg, #fef7f7 0%, #fdf2f2 100%);
  border: 2px solid #f1aeb5;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.1);
  margin-bottom: 30px;
  text-align: left;
}

.unavailable-icon {
  font-size: 2.5rem;
  flex-shrink: 0;
  line-height: 1;
  opacity: 0.8;
}

.unavailable-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.unavailable-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #842029;
  margin: 0;
  line-height: 1.3;
}

.unavailable-text {
  font-size: 0.95rem;
  color: #6f2832;
  line-height: 1.5;
  margin: 0;
}

.onboarded-icon {
  font-size: 3rem;
  flex-shrink: 0;
  line-height: 1;
}

.onboarded-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.onboarded-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #0c4a6e;
  margin: 0;
  line-height: 1.3;
}

.onboarded-text {
  font-size: 0.95rem;
  color: #0c4a6e;
  line-height: 1.5;
  margin: 0;
}

.onboarded-current-value {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid #bae6fd;
}

.current-value-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #0c4a6e;
}

.current-value-amount {
  font-size: 1.15rem;
  font-weight: 700;
}

.onboarded-note {
  font-size: 0.85rem;
  color: #075985;
  font-style: italic;
  margin: 0;
  padding: 8px 12px;
  background: rgba(186, 230, 253, 0.3);
  border-radius: 6px;
  border-left: 3px solid #0ea5e9;
}

.portfolio-overview-horizontal {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(125px, 1fr));
  gap: 15px;
  align-items: start;
}

.portfolio-metric-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  text-align: center;
  gap: 0px;
  padding: 3px 2px;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  min-height: 48px;
  height: 48px;
}

/* Asset Allocation Container */
.asset-allocation-container {
  width: 100%;
  margin-bottom: 25px;
}

.asset-allocation-card {
  min-height: 400px;
}

.asset-allocation-card .card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
}

.asset-allocation-card .card-header-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.asset-allocation-card .card-tabs {
  display: flex;
  gap: 8px;
  align-items: center;
}

.asset-allocation-table {
  width: 100%;
}

.allocation-table-header {
  display: grid;
  grid-template-columns: 1.3fr 1fr 1fr 1fr 1.5fr;
  gap: 15px;
  padding: 8px 0;
  border-bottom: 2px solid var(--an-primary);
  font-weight: 700;
  font-size: 0.85rem;
  color: var(--an-text-primary);
  text-transform: uppercase;
}

.allocation-table-body {
  overflow: visible;
}

.allocation-row {
  display: grid;
  grid-template-columns: 1.3fr 1fr 1fr 1fr 1.5fr;
  gap: 15px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  align-items: center;
  transition: background-color 0.2s;
}

.allocation-row:hover {
  background-color: #f8f9fa;
}

.col-asset-name {
  font-weight: 600;
  font-size: 0.85rem;
  color: var(--an-text-primary);
}

.col-current,
.col-target,
.col-value {
  font-weight: 600;
  font-size: 0.85rem;
  text-align: center;
  padding: 6px 8px;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.col-value {
  color: var(--an-text-primary);
}

.col-status {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  align-items: center;
}

.status-deviation {
  text-align: center;
  padding: 4px 6px;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
  font-size: 0.85rem;
}

.status-badge-container {
  display: flex;
  justify-content: center;
}

.status-header {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  text-align: center;
}

.status-sub-header {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--an-text-primary);
  text-transform: uppercase;
}

.deviation-text {
  font-size: 0.85rem;
  font-weight: 600;
}



/* Missing Asset Classes Collapsible Section */
.missing-assets-section {
  border-top: 1px solid #e9ecef;
  padding-top: 15px;
}

.collapsible-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(135deg, #fef7f7 0%, #fdf2f2 100%);
  border: 1px solid #f1aeb5;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.collapsible-header:hover {
  background: linear-gradient(135deg, #fdf2f2 0%, #fcebeb 100%);
  border-color: #e9a3ab;
}

.collapsible-title-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.collapsible-icon {
  font-size: 12px;
  color: #842029;
  transition: transform 0.2s ease;
}

.collapsible-icon.rotated {
  transform: rotate(90deg);
}

.collapsible-title {
  font-size: 14px;
  font-weight: 700;
  color: #842029;
}

.missing-count-badge {
  background: #842029;
  color: white;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
}

.collapsible-subtitle {
  font-size: 12px;
  color: #6f2832;
  font-style: italic;
}

.collapsible-content {
  margin-top: 10px;
  padding: 16px;
  background: #fefefe;
  border: 1px solid #f1aeb5;
  border-top: none;
  border-radius: 0 0 8px 8px;
}

.missing-assets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 12px;
  margin-bottom: 15px;
}

.missing-asset-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #f1aeb5;
  border-radius: 6px;
  border-left: 3px solid #842029;
}

.missing-asset-name {
  display: flex;
  align-items: center;
  gap: 6px;
}

.asset-name {
  font-size: 13px;
  font-weight: 600;
  color: #842029;
}

.missing-indicator {
  font-size: 12px;
  filter: hue-rotate(15deg);
}

.suggested-allocation {
  display: flex;
  align-items: center;
  gap: 4px;
}

.allocation-label {
  font-size: 11px;
  color: #6f2832;
  font-weight: 600;
}

.allocation-value {
  font-size: 13px;
  font-weight: 700;
  color: #842029;
  background: #fff3cd;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #ffeaa7;
}

.missing-assets-note {
  padding: 8px 12px;
  background: #fff9c4;
  border: 1px solid #ffeaa7;
  border-radius: 6px;
  border-left: 3px solid #f39c12;
}

.note-text {
  font-size: 12px;
  color: #7d5a00;
  line-height: 1.4;
  font-style: italic;
}

.status-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-align: center;
}

.status-badge-success {
  background-color: #d4edda;
  color: #155724;
}

.status-badge-warning {
  background-color: #fff3cd;
  color: #856404;
}

.status-badge-error {
  background-color: #f8d7da;
  color: #721c24;
}

.status-badge-neutral {
  background-color: #e2e3e5;
  color: #383d41;
}

/* Sector Attribution Container */
.sector-attribution-container {
  width: 100%;
  margin-bottom: 30px;
}

/* Nifty Analysis Container */
.nifty-analysis-container {
  width: 100%;
}

.nifty-content-grid {
  display: grid;
  grid-template-columns: 1.5fr 3.5fr;
  gap: 20px;
  overflow: hidden;
  width: 100%;
}

.nifty-chart-section {
  background: white;
  border-radius: 10px;
  padding: 8px 10px 10px 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.comparison-section {
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0;
  overflow: hidden;
  min-width: 0; /* Ensures flex/grid item can shrink below content size */
}

/* Performance Metrics Container */
.performance-metrics-container {
  width: 100%;
}

/* Metrics Sections */
.metrics-section {
  margin-bottom: 30px;
}

.metrics-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--an-primary);
}

.metrics-section-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin: 0;
}

.trend-indicator {
  font-size: 0.9rem;
  font-weight: 600;
  font-style: italic;
}

/* Card Title with Tooltip */
.card-title-container {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
}

.tooltip-container {
  position: relative;
  display: inline-block;
}

.info-icon {
  cursor: help;
  font-size: 12px;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.info-icon:hover {
  opacity: 1;
}

.tooltip-content {
  visibility: hidden;
  width: 320px;
  background-color: #333;
  color: white;
  text-align: left;
  border-radius: 6px;
  padding: 12px;
  position: absolute;
  z-index: 1000;
  bottom: 125%;
  left: 50%;
  margin-left: -160px;
  opacity: 0;
  transition: opacity 0.3s;
  font-size: 12px;
  line-height: 1.4;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.tooltip-content::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: #333 transparent transparent transparent;
}

.tooltip-container:hover .tooltip-content {
  visibility: visible;
  opacity: 1;
}

/* Portfolio-specific tooltip styling */
.portfolio-tooltip {
  width: 280px;
  margin-left: -140px;
  font-size: 11px;
  line-height: 1.3;
}

.metric-label-with-tooltip .tooltip-container .info-icon {
  font-size: 8px;
  opacity: 0.6;
  line-height: 0.8;
  vertical-align: top;
  position: relative;
  top: -1px;
}

.metric-label-with-tooltip .tooltip-container .info-icon:hover {
  opacity: 1;
}

.nifty-header {
  text-align: center;
  margin-bottom: 4px;
}

.nifty-chart-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0;
  gap: 2px;
}

.chart-container-centered {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.nifty-donut-chart {
  width: 130px;
  height: 130px;
}

.chart-center-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  pointer-events: none;
  z-index: 10;
}

.center-line1 {
  font-size: 12px;
  font-weight: 700;
  color: #666666;
  line-height: 1.1;
}

.center-line2 {
  font-size: 14px;
  font-weight: 700;
  color: #666666;
  line-height: 1.2;
}

/* Top 6 Sectors Grid */
.top-sectors-grid-container {
  width: 100%;
  max-width: 280px;
}

.top-sectors-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin-bottom: 8px;
  margin-top: 0;
  text-align: center;
}

.sectors-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(3, 1fr);
  gap: 4px;
  width: 100%;
}

.sector-box {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 6px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  min-height: 38px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.sector-box:hover {
  background-color: #f8f9fa;
  border-color: var(--an-primary);
  transform: translateY(-2px);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
}

.sector-box-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
}

.sector-weight-badge {
  background: var(--an-primary);
  color: white;
  font-size: 9.5px;
  font-weight: 700;
  padding: 2px 5px;
  border-radius: 6px;
  min-width: 28px;
  text-align: center;
}

.sector-box-content {
  flex: 1;
  display: flex;
  align-items: center;
}

.sector-box .sector-name {
  font-size: 10.5px;
  font-weight: 600;
  color: var(--an-text-primary);
  line-height: 1.1;
  text-align: left;
}

.sector-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.sector-name {
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: var(--an-text-primary);
}

.sector-weight {
  font-size: 14px;
  font-weight: 700;
  color: var(--an-primary);
}

/* Nifty Header Title */
.nifty-header-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  line-height: 1.2;
}



/* Nifty Legend */
.nifty-legend {
  max-height: 200px;
  overflow-y: auto;
}

.legend-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 5px;
}

.legend-item {
  padding: 2px 0;
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 8px;
  display: inline-block;
}

.legend-text {
  color: var(--an-text-primary);
}

/* Collapsible Sector Classification */
.sector-classification {
  border: 1px solid #e5e7eb;
  border-radius: 5px;
}

.classification-header {
  background: var(--an-background-light);
  transition: background-color 0.2s;
}

.classification-header:hover {
  background: #f0f0f0;
}

.classification-content {
  border-top: 1px solid #e5e7eb;
}

.sector-item {
  padding: 5px 0;
  border-bottom: 1px solid #f0f0f0;
}

.sector-item:last-child {
  border-bottom: none;
}

.subcategories {
  color: var(--an-text-muted);
}

/* Layout Containers - Removed old portfolio container styles */

.performance-cards-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 15px;
}

/* Combined Performance Card Styling */
.combined-performance-card {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
}

.combined-performance-card .card-header {
  margin-bottom: 15px;
}

.combined-performance-card .tab-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.perf-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  min-height: 180px;
}

.portfolio-snapshot-card {
  min-height: auto;
}

.card-title {
  color: var(--an-text-primary);
  font-weight: 600;
  font-size: 1.1rem;
  display: block;
  margin-bottom: 4px;
  line-height: 1.3;
}

.card-subtitle {
  color: var(--an-text-muted);
  display: block;
  margin-bottom: 4px;
  font-size: 14px;
  font-weight: 700;
}

.card-content {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.card-value {
  font-size: 14px;
  font-weight: 700;
}

.fund-benchmark {
  color: var(--an-text-muted);
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
}

/* Portfolio Snapshot Styling */
.card-header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 15px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 15px;
}

/* Recommendation Button */
.recommendation-button-section {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 25px 0;
  margin-top: 20px;
  margin-bottom: 20px;
  border-top: 1px solid #e9ecef;
  border-bottom: 1px solid #e9ecef;
  background: linear-gradient(135deg, #fafbfc 0%, #f8f9fa 100%);
}

.recommendation-button {
  background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 30px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 16px rgba(255, 107, 53, 0.25);
}

.recommendation-button:hover {
  background: linear-gradient(135deg, #f7931e 0%, #ff6b35 100%);
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(255, 107, 53, 0.4);
}

.recommendation-button .button-icon {
  font-size: 1.2rem;
}

.recommendation-button .button-text {
  font-size: 0.9rem;
}

.recommendation-button .button-arrow {
  font-size: 1.1rem;
  transition: transform 0.2s ease;
}

.recommendation-button:hover .button-arrow {
  transform: translateX(4px);
}

.card-date {
  font-size: 12px;
  font-weight: 600;
  color: var(--an-text-muted);
  white-space: nowrap;
}

.portfolio-overview {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.portfolio-metric {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.metric-label-with-tooltip {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 2px;
  flex-shrink: 0;
  min-width: 70px;
  line-height: 0.9;
}

.metric-label-small {
  font-size: 8px;
  font-weight: 700;
  color: var(--an-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.1px;
  flex-shrink: 0;
  min-width: 70px;
  line-height: 0.9;
}

.metric-value-large {
  font-size: 12px;
  font-weight: 700;
  text-align: center;
  line-height: 0.9;
}

.metric-value-medium {
  font-size: 10px;
  font-weight: 700;
  text-align: center;
  line-height: 0.9;
}

.metric-value-small {
  font-size: 9px;
  font-weight: 600;
  color: var(--an-text-primary);
  text-align: center;
  line-height: 0.9;
}

/* Combined Contributor/Detractor Card Styling */
.contributor-detractor-card .card-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sub-card {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 12px;
  border-radius: 6px;
  background: #f8f9fa;
}

.sub-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.sub-card-label {
  font-size: 12px;
  font-weight: 700;
  color: var(--an-text-primary);
}

.sub-card-sector {
  font-size: 11px;
  font-weight: 600;
}

.sub-card-value {
  font-size: 14px;
  font-weight: 700;
  text-align: center;
}

.sub-card-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 4px 0;
}

/* Card Tabs Styling */
.card-tabs {
  display: flex;
  gap: 0;
  background: #f8f9fa;
  border-radius: 6px;
  padding: 2px;
}

.tab-button {
  flex: 1;
  padding: 8px 12px;
  background: transparent;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  color: var(--an-text-muted);
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
}

.tab-button:hover {
  background: rgba(255, 255, 255, 0.7);
  color: var(--an-text-primary);
}

.tab-button.tab-active {
  background: white;
  color: var(--an-primary);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.tab-content {
  width: 100%;
}

.card-subtitle {
  font-size: 14px;
  font-weight: 600;
  color: var(--an-text-primary);
}

/* Combined Risk Card Styling */
.combined-risk-card {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
}

.combined-risk-card .card-header {
  margin-bottom: 15px;
}

.combined-risk-card .tab-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Metric Display Styling */
.metric-display {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
  flex-wrap: wrap;
}

.metric-label {
  font-size: 12px;
  font-weight: normal;
  color: var(--an-text-primary);
}

.metric-separator {
  font-size: 12px;
  font-weight: normal;
  color: var(--an-text-primary);
}

.metric-date {
  font-size: 12px;
  font-weight: normal;
  color: var(--an-text-primary);
}

.metric-value {
  font-size: 14px;
  font-weight: 700;
  color: #000000;
}

/* Mini Chart Styling */
.mini-chart {
  height: 150px;
  width: 100%;
  margin-top: 1px;
}

.performance-mini-chart {
  height: 100%;
  width: 100%;
}

.fund-benchmark {
  font-size: 12px;
  line-height: 1.4;
}

/* Additional Color Classes */
.text-warning {
  color: var(--an-warning);
}

.text-info {
  color: var(--an-info);
}

/* Color Classes */
.text-success {
  color: var(--an-success);
}

.text-error {
  color: var(--an-error);
}

.text-muted {
  color: var(--an-text-muted);
}

.text-primary {
  color: var(--an-primary);
}

/* Date Footer */
.date-footer {
  text-align: right;
  padding-top: 15px;
  border-top: 1px solid #f0f0f0;
}

.date-footer span {
  font-size: 14px;
  font-weight: 700;
}

/* Background Colors */
.bg-light {
  background-color: var(--an-background-light);
}

.bg-white {
  background-color: white;
}

.bg-secondary {
  background-color: #f8f9fa;
}

/* Section Container Styling */
.portfolio-analytics-page {
  width: 100%;
  height: 100%;
}

.sector-analytics-detail-page {
  width: 100%;
  height: 100%;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .performance-cards-grid {
    grid-template-columns: 1.8fr 1fr 1fr 1fr;
    gap: 12px;
  }
}

@media (max-width: 1200px) {
  .nifty-content-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}

@media (max-width: 1024px) {
  .portfolio-overview-horizontal {
    grid-template-columns: repeat(3, 1fr);
    gap: 15px;
  }

  .nifty-content-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .sector-performance-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }

  .performance-cards-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  /* Asset allocation table adjustments for tablet */
  .allocation-table-header,
  .allocation-row {
    grid-template-columns: 1.4fr 1fr 0.8fr 0.8fr 1.2fr;
    gap: 10px;
    padding: 8px 0;
    font-size: 0.85rem;
  }

  .col-asset-name,
  .col-current,
  .col-target,
  .col-value,
  .status-deviation {
    font-size: 0.85rem;
  }

  .status-badge {
    font-size: 0.7rem;
    padding: 2px 6px;
  }
}

@media (max-width: 768px) {
  .nifty-content-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .nifty-chart-wrapper {
    gap: 20px;
  }

  .nifty-donut-chart {
    width: 200px;
    height: 200px;
  }

  .top-sectors-grid-container {
    max-width: 100%;
  }

  .sectors-grid {
    gap: 8px;
  }

  .sector-box {
    padding: 8px;
    min-height: 60px;
  }

  .sector-box .sector-name {
    font-size: 10px;
  }

  .sector-weight-badge {
    font-size: 9px;
    padding: 2px 5px;
  }

  .sector-performance-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .performance-cards-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  /* Asset allocation table - mobile card layout */
  .allocation-table-header {
    display: none; /* Hide header on mobile */
  }

  .allocation-table-body {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .allocation-row {
    display: flex;
    flex-direction: column;
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 12px;
    gap: 8px;
    grid-template-columns: none;
  }

  .allocation-row:hover {
    background-color: #f0f0f0;
  }

  .col-asset-name {
    font-size: 1rem;
    font-weight: 700;
    color: var(--an-primary);
    margin-bottom: 4px;
    border-bottom: 1px solid #dee2e6;
    padding-bottom: 4px;
  }

  .col-value,
  .col-current,
  .col-target {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;
    font-size: 0.9rem;
    background: transparent;
    border: none;
    border-radius: 0;
  }

  .col-value::before {
    content: "Market Value:";
    font-weight: 600;
    color: var(--an-text-muted);
  }

  .col-current::before {
    content: "Current Allocation:";
    font-weight: 600;
    color: var(--an-text-muted);
  }

  .col-target::before {
    content: "Target Allocation:";
    font-weight: 600;
    color: var(--an-text-muted);
  }

  .col-status {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    padding-top: 8px;
    border-top: 1px solid #dee2e6;
    grid-template-columns: none;
  }

  .status-deviation {
    background: transparent;
    border: none;
    padding: 4px 8px;
    font-size: 0.9rem;
    border-radius: 4px;
    background: #e9ecef;
  }

  .status-badge {
    font-size: 0.8rem;
    padding: 4px 8px;
  }

  .missing-assets-grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .missing-asset-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .weight-comparison-grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }
}

@media (max-width: 768px) {
  .newly-onboarded-message {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 16px;
    padding: 20px;
  }

  .metrics-unavailable-message {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 12px;
    padding: 20px;
  }

  .unavailable-icon {
    font-size: 2rem;
  }

  .unavailable-title {
    font-size: 1rem;
  }

  .unavailable-text {
    font-size: 0.9rem;
  }

  .onboarded-icon {
    font-size: 2.5rem;
  }

  .onboarded-title {
    font-size: 1.1rem;
  }

  .onboarded-text {
    font-size: 0.9rem;
  }

  .onboarded-current-value {
    flex-direction: column;
    gap: 6px;
  }

  .current-value-label {
    font-size: 0.85rem;
  }

  .current-value-amount {
    font-size: 1.1rem;
  }

  .onboarded-note {
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .portfolio-overview-horizontal {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .portfolio-metric-col {
    padding: 8px 6px;
  }

  .newly-onboarded-message {
    padding: 16px;
    gap: 12px;
  }

  .onboarded-icon {
    font-size: 2rem;
  }

  .onboarded-title {
    font-size: 1rem;
  }

  .onboarded-text,
  .onboarded-note {
    font-size: 0.8rem;
  }

  .onboarded-current-value {
    padding: 10px 12px;
  }

  .current-value-amount {
    font-size: 1rem;
  }

  .sector-performance-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .nifty-chart-section,
  .comparison-section {
    padding: 15px;
  }

  .nifty-donut-chart {
    width: 180px;
    height: 180px;
  }

  .top-sectors-grid-container {
    max-width: 100%;
  }

  .sectors-grid {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(3, 1fr);
    gap: 6px;
  }

  .sector-box {
    padding: 6px;
    min-height: 55px;
  }

  .sector-box .sector-name {
    font-size: 9px;
  }

  .sector-weight-badge {
    font-size: 8px;
    padding: 1px 4px;
    min-width: 28px;
  }

  .sector-dot {
    width: 6px;
    height: 6px;
  }

  .perf-card {
    padding: 10px;
    min-height: 200px;
  }

  .mini-chart {
    height: 130px;
  }

  /* Keep mobile card layout for allocation table */
  .allocation-row {
    padding: 10px;
    gap: 6px;
  }

  .col-asset-name {
    font-size: 0.9rem;
  }

  .col-value,
  .col-current,
  .col-target {
    font-size: 0.85rem;
    padding: 4px 0;
  }

  .status-deviation {
    font-size: 0.85rem;
  }

  .status-badge {
    font-size: 0.75rem;
    padding: 3px 6px;
  }

  .asset-allocation-card {
    min-height: 350px;
  }

  .allocation-table-body {
    max-height: none; /* Remove height restriction on mobile */
  }
}
</style>
